/**
 * Doctors, patients and services reports (one row each, with the orders behind every row).
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, GridView as CardsIcon, TableRows as TableIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import DetailedReportTable, { type ReportColumn } from './DetailedReportTable';
import ReportOrdersDialog, { type ReportOrdersFilter } from './ReportOrdersDialog';
import ReportSummaryCards from './ReportSummaryCards';
import ServicesCards from './ServicesCards';
import { downloadCsv } from '../../utils/csv';
import { downloadBlob } from '../../utils/download';
import { activeChip, metricColumns } from './reportColumns';
import {
  useGetDoctorsReportQuery,
  useGetPatientsReportQuery,
  useGetServicePerformanceReportQuery,
  useLazyExportServiceReportQuery,
  useLazyExportPatientsReportQuery,
  type DoctorReportRow,
  type PatientReportRow,
  type ReportPeriod,
  type ServiceReportRow,
} from '../../store/api/detailedReportsApi';

interface ReportProps {
  period: ReportPeriod;
  canExport: boolean;
}

const fileSuffix = (period: ReportPeriod) => `${period.date_from || 'all'}_${period.date_to || 'all'}`;

/** Row click → dialog with the orders behind that row */
const useDrillDown = () => {
  const [state, setState] = useState<{
    title: string;
    filter: ReportOrdersFilter;
  } | null>(null);
  return {
    open: (title: string, filter: ReportOrdersFilter) => setState({ title, filter }),
    dialog: (period: ReportPeriod) =>
      state && (
        <ReportOrdersDialog
          open
          title={state.title}
          filter={state.filter}
          period={period}
          onClose={() => setState(null)}
        />
      ),
  };
};

const ReportHeader = ({ title, hint }: { title: string; hint: string }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6">{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {hint}
    </Typography>
  </Box>
);

/** Download one service's Excel workbook; `exportingId` drives the spinner on its button */
const useServiceExport = (period: ReportPeriod) => {
  const { t, i18n } = useTranslation();
  const [fetchWorkbook] = useLazyExportServiceReportQuery();
  const [exportingId, setExportingId] = useState<number | null>(null);

  const run = async (row: ServiceReportRow, label: string) => {
    setExportingId(row.id);
    try {
      const blob = await fetchWorkbook({
        subservice_id: row.id,
        lang: i18n.language?.startsWith('ar') ? 'ar' : 'en',
        ...period,
      }).unwrap();
      downloadBlob(blob, `${label.replace(/[\\/:*?"<>|]+/g, '-')}_${fileSuffix(period)}.xlsx`);
    } catch {
      toast.error(t('reports.toast.exportError'));
    } finally {
      setExportingId(null);
    }
  };
  return { run, exportingId };
};

// ------------------------------------------------------------------ doctors

export const DoctorsReport = ({
  period,
  canExport,
  providerType,
}: ReportProps & { providerType?: 'SPECIALIST' | 'CONSULTANT' }) => {
  const { t } = useTranslation();
  const { data, isFetching } = useGetDoctorsReportQuery({
    ...period,
    provider_type: providerType,
  });
  const drill = useDrillDown();

  const columns = useMemo<ReportColumn<DoctorReportRow>[]>(
    () => [
      {
        key: 'full_name',
        label: t('reports.detailed.cols.doctor'),
        value: (r) => r.full_name,
        render: (r) => (
          <Box>
            <Typography variant="body2" fontWeight={600} component="span">
              {r.full_name}
            </Typography>
            {activeChip(t, r.is_active)}
            <Typography variant="caption" color="text.secondary" display="block" dir="ltr" sx={{ textAlign: 'start' }}>
              {r.phone_number}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'provider_type',
        label: t('reports.detailed.cols.type'),
        value: (r) => t(`reports.providerType.${r.provider_type}`),
        render: (r) => (
          <Chip
            size="small"
            variant="outlined"
            color={r.provider_type === 'SPECIALIST' ? 'primary' : 'secondary'}
            label={t(`reports.providerType.${r.provider_type}`)}
          />
        ),
      },
      {
        key: 'specialization',
        label: t('reports.detailed.cols.speciality'),
        value: (r) => r.specialization || null,
      },
      {
        key: 'service_areas',
        label: t('reports.detailed.cols.areas'),
        value: (r) => r.service_areas.join('، ') || null,
      },
      ...metricColumns<DoctorReportRow>(t, { providerRating: true }),
    ],
    [t],
  );

  return (
    <CardContent>
      <ReportHeader title={t('reports.detailed.doctors.title')} hint={t('reports.detailed.doctors.hint')} />
      <ReportSummaryCards summary={data?.data.summary} showUnassigned />
      <DetailedReportTable
        rows={data?.data.rows ?? []}
        columns={columns}
        rowKey={(r) => `${r.provider_type}-${r.id}`}
        searchText={(r) => `${r.full_name} ${r.phone_number} ${r.specialization} ${r.service_areas.join(' ')}`}
        searchPlaceholder={t('reports.detailed.doctors.search')}
        exportFileName={`doctors_report_${fileSuffix(period)}`}
        canExport={canExport}
        loading={isFetching}
        defaultSort={{ key: 'total_orders', direction: 'desc' }}
        onRowClick={(r) =>
          drill.open(t('reports.detailed.ordersOf', { name: r.full_name }), {
            provider_type: r.provider_type,
            provider_id: r.id,
          })
        }
      />
      {drill.dialog(period)}
    </CardContent>
  );
};

// ----------------------------------------------------------------- patients

export const PatientsReport = ({ period, canExport }: ReportProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');
  const { data, isFetching } = useGetPatientsReportQuery(period);
  const drill = useDrillDown();
  const summary = data?.data.summary;
  const [fetchWorkbook, { isFetching: exporting }] = useLazyExportPatientsReportQuery();

  const handleExport = async () => {
    try {
      const blob = await fetchWorkbook({ ...period, lang: isAr ? 'ar' : 'en' }).unwrap();
      downloadBlob(blob, `patients_report_${fileSuffix(period)}.xlsx`);
    } catch {
      toast.error(t('reports.toast.exportError'));
    }
  };

  const columns = useMemo<ReportColumn<PatientReportRow>[]>(
    () => [
      {
        key: 'full_name',
        label: t('reports.detailed.cols.patient'),
        value: (r) => r.full_name,
        render: (r) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {r.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" dir="ltr" sx={{ textAlign: 'start' }}>
              {r.phone_number}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'has_account',
        label: t('reports.detailed.cols.account'),
        value: (r) => (r.has_account ? t('common.yes') : t('common.no')),
      },
      {
        key: 'lead_source',
        label: t('reports.detailed.cols.leadSource'),
        value: (r) => (r.lead_source ? t(`users.leadSources.${r.lead_source}`) : null),
      },
      {
        key: 'services_used',
        label: t('reports.detailed.cols.servicesUsed'),
        align: 'center',
        value: (r) => r.services_used,
      },
      ...metricColumns<PatientReportRow>(t),
    ],
    [t],
  );

  return (
    <CardContent>
      <ReportHeader title={t('reports.detailed.patients.title')} hint={t('reports.detailed.patients.hint')} />
      <ReportSummaryCards summary={summary} />
      {!!summary?.orders_without_patient_file && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('reports.detailed.patients.unlinkedOrders', {
            count: summary.orders_without_patient_file,
          })}
        </Alert>
      )}
      <DetailedReportTable
        rows={data?.data.rows ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        searchText={(r) => `${r.full_name} ${r.phone_number}`}
        searchPlaceholder={t('reports.detailed.patients.search')}
        exportFileName={`patients_report_${fileSuffix(period)}`}
        canExport={canExport}
        onExport={handleExport}
        exporting={exporting}
        loading={isFetching}
        defaultSort={{ key: 'completed_value', direction: 'desc' }}
        onRowClick={(r) =>
          drill.open(t('reports.detailed.ordersOf', { name: r.full_name }), {
            patient_id: r.id,
          })
        }
      />
      {drill.dialog(period)}
    </CardContent>
  );
};

// ----------------------------------------------------------------- services

export const ServicesPerformanceReport = ({ period, canExport }: ReportProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');
  const { data, isFetching } = useGetServicePerformanceReportQuery(period);
  const drill = useDrillDown();

  const [view, setView] = useState<'cards' | 'table'>('cards');
  const serviceExport = useServiceExport(period);

  const name = (r: ServiceReportRow) => (isAr && r.name_ar ? r.name_ar : r.name);
  const serviceName = (r: ServiceReportRow) => (isAr && r.service_name_ar ? r.service_name_ar : r.service_name);

  const columns = useMemo<ReportColumn<ServiceReportRow>[]>(
    () => [
      {
        key: 'name',
        label: t('reports.detailed.cols.subservice'),
        value: (r) => name(r),
        render: (r) => (
          <Box>
            <Typography variant="body2" fontWeight={600} component="span">
              {name(r)}
            </Typography>
            {activeChip(t, r.is_active)}
            {r.parent_name && (
              <Typography variant="caption" color="text.secondary" display="block">
                {r.parent_name}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'service_name',
        label: t('reports.detailed.cols.service'),
        value: (r) => serviceName(r),
      },
      {
        key: 'patients_count',
        label: t('reports.detailed.cols.patients'),
        align: 'center',
        value: (r) => r.patients_count,
      },
      {
        key: 'providers_count',
        label: t('reports.detailed.cols.providers'),
        align: 'center',
        value: (r) => r.providers_count,
      },
      ...metricColumns<ServiceReportRow>(t, { providerRating: true }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isAr],
  );
  // Per-row workbook button (table view); not part of the CSV
  const tableColumns: ReportColumn<ServiceReportRow>[] = canExport
    ? [
        ...columns,
        {
          key: 'export',
          label: '',
          align: 'center',
          value: () => '',
          render: (r) => (
            <Tooltip title={t('reports.detailed.services.exportOne')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={serviceExport.exportingId !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    serviceExport.run(r, name(r));
                  }}
                >
                  {serviceExport.exportingId === r.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ),
        },
      ]
    : columns;

  return (
    <CardContent>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <ReportHeader title={t('reports.detailed.services.title')} hint={t('reports.detailed.services.hint')} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* The table view has its own export (it follows the search box) */}
          {canExport && view === 'cards' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!data?.data.rows.length}
              onClick={() => downloadCsv(columns, data?.data.rows ?? [], `services_report_${fileSuffix(period)}`)}
            >
              {t('reports.detailed.exportExcel')}
            </Button>
          )}
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="cards" sx={{ gap: 0.5 }}>
              <CardsIcon fontSize="small" />
              {t('reports.detailed.services.viewCards')}
            </ToggleButton>
            <ToggleButton value="table" sx={{ gap: 0.5 }}>
              <TableIcon fontSize="small" />
              {t('reports.detailed.services.viewTable')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <ReportSummaryCards summary={data?.data.summary} />
      {view === 'cards' ? (
        isFetching && !data ? null : (
          <ServicesCards
            rows={data?.data.rows ?? []}
            name={name}
            serviceName={serviceName}
            canExport={canExport}
            exportingId={serviceExport.exportingId}
            onExport={(r) => serviceExport.run(r, name(r))}
            onOpen={(r) =>
              drill.open(t('reports.detailed.ordersOf', { name: name(r) }), {
                subservice_id: r.id,
              })
            }
          />
        )
      ) : (
        <DetailedReportTable
          rows={data?.data.rows ?? []}
          columns={tableColumns}
          exportColumns={columns}
          rowKey={(r) => r.id}
          searchText={(r) => `${r.name} ${r.name_ar} ${r.service_name} ${r.service_name_ar} ${r.parent_name ?? ''}`}
          searchPlaceholder={t('reports.detailed.services.search')}
          exportFileName={`services_report_${fileSuffix(period)}`}
          canExport={canExport}
          loading={isFetching}
          defaultSort={{ key: 'completed_value', direction: 'desc' }}
          onRowClick={(r) =>
            drill.open(t('reports.detailed.ordersOf', { name: name(r) }), {
              subservice_id: r.id,
            })
          }
        />
      )}
      {drill.dialog(period)}
    </CardContent>
  );
};
